import { t, type TSchema, type Static } from 'elysia';
import type { TObject } from '@sinclair/typebox';

export { t };
export type { TSchema, Static };

function stripDefaultsNode(node: unknown, isPropertyMap = false): unknown {
  if (Array.isArray(node)) return node.map((item) => stripDefaultsNode(item));
  if (node === null || typeof node !== 'object') return node;
  // Object.assign preserves TypeBox's enumerable Kind/Hint symbols.
  const clone = Object.assign({}, node) as Record<string, unknown>;
  if (!isPropertyMap) delete clone.default;
  for (const [key, value] of Object.entries(clone)) {
    // examples/const/enum hold raw data values, not schemas — leave intact.
    if (!isPropertyMap && (key === 'examples' || key === 'const' || key === 'enum')) continue;
    // A properties map may legitimately contain a field named `default`.
    const childIsPropertyMap =
      !isPropertyMap && (key === 'properties' || key === 'patternProperties');
    clone[key] = stripDefaultsNode(value, childIsPropertyMap);
  }
  return clone;
}

/**
 * Deep clone of a schema with every `default` annotation removed.
 *
 * For PATCH bodies: `t.Partial(CreateSchema)` keeps the create-time
 * `default` annotations, and Elysia fills them into every partial body —
 * silently resetting columns the caller never sent (confirmed live on
 * provider PATCH 2026-06-12). `t.Partial(stripDefaults(CreateSchema))`
 * keeps create semantics intact while making absent-means-unchanged hold
 * for updates. The walk is deep because Elysia's coercion types
 * (e.g. `t.Integer`) duplicate the default inside nested anyOf members.
 */
export function stripDefaults<T extends TObject>(schema: T): T {
  return stripDefaultsNode(schema) as T;
}

/**
 * Minimum length for any NEW password, restated for the schemas that both a
 * browser and a service validate against.
 *
 * The source of truth is `PASSWORD_MIN_LENGTH` in
 * `packages/shared/src/crypto/password-policy.ts`. It is restated rather than
 * imported because `@b1fr0st/contracts` is bundled into both SPAs and does not
 * depend on `@b1fr0st/shared`; taking that dependency to carry one integer would
 * churn `bun.lock` for every workspace member. `password-min-length-sites.test.ts`
 * in @b1fr0st/shared fails if this number ever stops matching, and also fails on
 * any new password schema that hardcodes a number instead of using a constant.
 */
export const PASSWORD_MIN_LENGTH = 6;

/**
 * Minimum length for a STAFF or PARTNER password — backoffice admins, node
 * owners and partners. Higher than `PASSWORD_MIN_LENGTH` on purpose: those
 * accounts move money. Restated from `@b1fr0st/shared` for the same reason as
 * the player floor above, and pinned by the same test.
 */
export const STAFF_PASSWORD_MIN_LENGTH = 8;

/** ISO-8601 date-time string */
export const DateTimeISO = t.String({
  format: 'date-time',
  examples: ['2026-04-21T10:00:00.000Z'],
});

export const UUID = t.String({
  format: 'uuid',
  examples: ['018f2d2b-1f99-7c0a-8fae-0a1c26e1e4a9'],
});

export const Money = t.Integer({
  minimum: 0,
  description: 'Amount in minor currency unit (e.g. satang for THB, cents for USD)',
  examples: [50000],
});

export const Status = t.Union([t.Literal('active'), t.Literal('inactive')]);

export const Pagination = t.Object({
  page: t.Integer({ minimum: 1, default: 1 }),
  pageSize: t.Integer({ minimum: 1, maximum: 1000, default: 20 }),
  sortBy: t.Optional(t.String()),
  sortDir: t.Union([t.Literal('asc'), t.Literal('desc')], { default: 'desc' }),
});

export const PaginatedResponse = <T extends TSchema>(item: T) =>
  t.Object({
    items: t.Array(item),
    page: t.Integer(),
    pageSize: t.Integer(),
    total: t.Integer(),
    totalPages: t.Integer(),
  });

export const ErrorResponse = t.Object(
  {
    type: t.String({ format: 'uri' }),
    title: t.String(),
    status: t.Integer(),
    code: t.String(),
    detail: t.Optional(t.String()),
    instance: t.Optional(t.String()),
    extensions: t.Optional(t.Record(t.String(), t.Unknown())),
  },
  { $id: 'ErrorResponse' },
);

export type ErrorResponseShape = Static<typeof ErrorResponse>;

export const OkResponse = t.Object({ ok: t.Literal(true) });

/* -------- Permission resource & action constants -------- */

export const PERMISSION_ACTIONS = [
  'read',
  'create',
  'update',
  'delete',
  'export',
  'approve',
  'reject',
  'toggle_status',
  'reset_password',
  'release_condition',
  'move_member',
  'settle_result',
  'copy',
  'clone',
  'revoke',
  'bootstrap',
] as const;

export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export const ADMIN_LEVELS = {
  VIEWER: 1,
  STAFF: 2,
  SUPERVISOR: 3,
  MANAGER: 4,
  SUPERADMIN: 5,
  ROOT: 6,
} as const;

export type AdminLevel = (typeof ADMIN_LEVELS)[keyof typeof ADMIN_LEVELS];
